import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';

export interface FilterDropdownOption {
  value: string;
  label: string;
}

interface FilterDropdownProps {
  placeholder: string;
  value: string;
  options: FilterDropdownOption[];
  onChange: (value: string) => void;
  className?: string;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  placeholder,
  value,
  options,
  onChange,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <div
        className={`flex h-10 min-w-[120px] items-center rounded-md border bg-white text-sm ${
          selected ? 'border-gray-400 pl-3 pr-1' : 'border-gray-300 px-3'
        }`}
      >
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="flex flex-1 items-center justify-between gap-2 text-left text-gray-700"
        >
          <span className={selected ? 'font-medium text-gray-900' : 'text-gray-600'}>
            {selected ? selected.label : placeholder}
          </span>
          {!selected && <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />}
        </button>
        {selected && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
              setOpen(false);
            }}
            className="ml-1 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label={`Clear ${placeholder} filter`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {open && options.length > 0 && (
        <ul className="absolute left-0 top-full z-20 mt-1 max-h-60 min-w-full overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {options.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                  option.value === value ? 'font-medium text-[#655FA8]' : 'text-gray-700'
                }`}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FilterDropdown;
